import type { Message } from '../types/sessions.js';
import type { SDKStorage } from '../storage/types.js';
import { DurableCache } from './durable.js';

/**
 * History cache — the loaded page set per session.
 *
 * One entry per (account identity, workspace lane, session, participant): the tail page
 * (the newest `latest=true` read) plus the older pages fetched by upward
 * scroll, keyed by the cursor that produced them. The entry is what a re-open
 * paints first: reads serve the stored page set and revalidate behind
 * it — the tail refetches on any staleness trigger, older pages below the
 * live edge are trusted on read (no age check anywhere — sdk ADR-4f834).
 *
 * `result://` envelope handles pass through verbatim, never re-resolved;
 * entries never cross accounts (an identity change evicts all) and the
 * footprint is one LRU whose cap is derived from client resources.
 */

export interface CachedTailPage {
  messages: Message[];
  fetchedAt: number;
}

export interface CachedOlderPage {
  /** The `beforeMessageIndex` cursor that produced this page. */
  beforeMessageIndex: number;
  messages: Message[];
  fetchedAt: number;
}

export interface HistoryPageSet {
  tail: CachedTailPage;
  olderPages: CachedOlderPage[];
  /** True when a backward page came back short — the transcript start is loaded. */
  exhausted: boolean;
}

/**
 * Whose conversation a page set holds: a bare session id is the primary
 * participant's transcript (the chat); a subagent participant's conversation
 * names the participant too.
 */
export type HistoryLane = string | { sessionId: string; participantId: string };

export function historyCacheEntryKey(
  accountId: string,
  workspaceId: string | null | undefined,
  sessionId: string,
  participantId?: string,
): string {
  const primary = `${accountId}|${workspaceId ?? ''}|${sessionId}`;
  return participantId === undefined ? primary : `${primary}|${participantId}`;
}

function laneKey(accountId: string, workspaceId: string | null | undefined, lane: HistoryLane): string {
  return typeof lane === 'string'
    ? historyCacheEntryKey(accountId, workspaceId, lane)
    : historyCacheEntryKey(accountId, workspaceId, lane.sessionId, lane.participantId);
}

/**
 * The entry cap derives from the client's memory budget, not a product
 * number. `navigator.deviceMemory` (Chromium web + desktop renderer) reports
 * GiB rounded to a power of two; where absent (Node test tier, non-Chromium),
 * the 4 GiB class is the conservative baseline. A page set is bounded by the
 * 200-row tail page plus scrolled older pages, ~1 KiB per row upper bound per
 * message, so 1 GiB of budget buys ~256 page sets.
 */
export function deriveHistoryCacheCap(deviceMemoryGiB?: number): number {
  const giB =
    deviceMemoryGiB ??
    (typeof navigator !== 'undefined' ? (navigator as { deviceMemory?: number }).deviceMemory : undefined) ??
    4;
  const budgetBytes = giB * 1024 * 1024 * 1024;
  const perEntryBytes = 4 * 1024 * 1024;
  return Math.max(8, Math.floor(budgetBytes / perEntryBytes));
}

interface Entry extends HistoryPageSet {
  lastReadAt: number;
}

export class HistoryCache {
  private entries = new Map<string, Entry>();
  private lastIdentity: string | null = null;
  private durable: DurableCache<HistoryPageSet>;

  constructor(
    private capEntries: number = deriveHistoryCacheCap(),
    storage: SDKStorage | null = null,
  ) {
    this.durable = new DurableCache<HistoryPageSet>(storage, 'history:', (key, value) => {
      // Hydration re-inserts WITHOUT re-writing the mirror.
      this.entries.set(key, { ...value, lastReadAt: 0 });
    });
  }

  /** Entries never cross accounts (sdk ADR-a9d91). */
  prepare(identity: string): void {
    if (this.lastIdentity === identity) return;
    if (this.lastIdentity !== null) {
      this.entries.clear();
      this.durable.evictIdentityDurable(this.lastIdentity);
    }
    this.lastIdentity = identity;
    this.durable.bind(identity);
  }

  lookup(accountId: string, workspaceId: string | null | undefined, lane: HistoryLane): HistoryPageSet | null {
    this.prepare(accountId);
    const key = laneKey(accountId, workspaceId, lane);
    const entry = this.entries.get(key);
    if (!entry) return null;
    // Map iteration order is insertion order — re-insert to touch LRU recency.
    this.entries.delete(key);
    entry.lastReadAt = Date.now();
    this.entries.set(key, entry);
    return { tail: entry.tail, olderPages: entry.olderPages, exhausted: entry.exhausted };
  }

  /** Atomic replace of the whole page set (revalidate success). */
  store(
    accountId: string,
    workspaceId: string | null | undefined,
    lane: HistoryLane,
    pageSet: HistoryPageSet,
  ): void {
    this.prepare(accountId);
    this.insert(laneKey(accountId, workspaceId, lane), {
      ...pageSet,
      olderPages: [...pageSet.olderPages],
      lastReadAt: Date.now(),
    });
  }

  /** Store one backward page from `loadOlderMessages` (trusted on read). */
  appendOlderPage(
    accountId: string,
    workspaceId: string | null | undefined,
    lane: HistoryLane,
    beforeMessageIndex: number,
    messages: Message[],
  ): void {
    this.prepare(accountId);
    const key = laneKey(accountId, workspaceId, lane);
    const entry = this.entries.get(key);
    if (!entry) return;
    this.insert(key, {
      ...entry,
      olderPages: [...entry.olderPages, { beforeMessageIndex, messages, fetchedAt: Date.now() }],
      lastReadAt: Date.now(),
    });
  }

  /**
   * Revalidate result: replace the tail atomically and drop older pages at or
   * above the new tail's window — they are inside it now; pages below it stay
   * (trusted on read).
   */
  replaceTail(
    accountId: string,
    workspaceId: string | null | undefined,
    lane: HistoryLane,
    tail: CachedTailPage,
    exhausted: boolean,
  ): void {
    this.prepare(accountId);
    const key = laneKey(accountId, workspaceId, lane);
    const entry = this.entries.get(key);
    const tailMin = tail.messages.length > 0 ? tail.messages[0].message_index : Infinity;
    this.insert(key, {
      tail,
      exhausted,
      olderPages: (entry?.olderPages ?? []).filter(
        (p) => p.messages.length > 0 && p.messages[0].message_index < tailMin,
      ),
      lastReadAt: Date.now(),
    });
  }

  extendTail(
    accountId: string,
    workspaceId: string | null | undefined,
    lane: HistoryLane,
    messages: Message[],
  ): void {
    this.prepare(accountId);
    const key = laneKey(accountId, workspaceId, lane);
    const entry = this.entries.get(key);
    if (!entry) return;
    const tailMax = entry.tail.messages.reduce((max, m) => Math.max(max, m.message_index), 0);
    this.insert(key, {
      ...entry,
      tail: {
        messages: [...entry.tail.messages, ...messages.filter((m) => m.message_index > tailMax)],
        fetchedAt: Date.now(),
      },
      lastReadAt: Date.now(),
    });
  }

  /**
   * Rewind: drop the owning history's pages at/above the rewind point —
   * the transcript there no longer exists.
   */
  dropAtOrAbove(
    accountId: string,
    workspaceId: string | null | undefined,
    lane: HistoryLane,
    messageIndex: number,
  ): void {
    this.prepare(accountId);
    const key = laneKey(accountId, workspaceId, lane);
    const entry = this.entries.get(key);
    if (!entry) return;
    const tail = {
      ...entry.tail,
      messages: entry.tail.messages.filter((m) => m.message_index < messageIndex),
    };
    this.insert(key, {
      ...entry,
      tail,
      olderPages: entry.olderPages.filter(
        (p) => p.messages.length > 0 && p.messages[0].message_index < messageIndex,
      ),
      exhausted: false,
      lastReadAt: Date.now(),
    });
  }

  /**
   * Window release: keep only rows at/above `messageIndex`, the lowest row
   * still loaded contiguously with the tail (sdk ADR-f43c7).
   */
  dropBelow(
    accountId: string,
    workspaceId: string | null | undefined,
    lane: HistoryLane,
    messageIndex: number,
  ): void {
    this.prepare(accountId);
    const key = laneKey(accountId, workspaceId, lane);
    const entry = this.entries.get(key);
    if (!entry) return;
    const tailMessages = entry.tail.messages.filter((m) => m.message_index >= messageIndex);
    const olderPages = entry.olderPages.filter(
      (p) => p.messages.length > 0 && p.messages[0].message_index >= messageIndex,
    );
    const dropped =
      tailMessages.length !== entry.tail.messages.length || olderPages.length !== entry.olderPages.length;
    if (!dropped) return;
    this.insert(key, {
      ...entry,
      tail: { ...entry.tail, messages: tailMessages },
      olderPages,
      exhausted: false,
      lastReadAt: Date.now(),
    });
  }

  evictAll(): void {
    this.entries.clear();
    this.durable.evictAllDurable();
  }

  private insert(key: string, entry: Entry): void {
    this.entries.delete(key);
    this.entries.set(key, entry);
    // Write-through the durable mirror: the snapshot IS the
    // page set, keyed account-scoped.
    this.durable.writeThrough(key, {
      tail: entry.tail,
      olderPages: entry.olderPages,
      exhausted: entry.exhausted,
    });
    while (this.entries.size > this.capEntries) {
      const lru = this.entries.keys().next();
      if (lru.done) break;
      this.entries.delete(lru.value);
      this.durable.remove(lru.value);
    }
  }
}
